import { useState, useEffect, useRef, useCallback } from "react";
import mermaid from "mermaid";

const useProfilNCBContent = () => {
  const [expandedSections, setExpandedSections] = useState({
    bagjatranin: false,
    bagkominter: false,
    bagbatanas: false,
  });
  const [selectedNode, setSelectedNode] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const chartRef = useRef(null);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleNodeClick = useCallback((nodeId) => {
    setSelectedNode(nodeId);
    // Auto-expand the corresponding section
    if (
      nodeId === "bagjatranin" ||
      nodeId === "bagkominter" ||
      nodeId === "bagbatanas"
    ) {
      setExpandedSections((prev) => ({
        ...prev,
        [nodeId]: true,
      }));

      // Smooth scroll to the section with navbar offset
      setTimeout(() => {
        const element = document.getElementById(`section-${nodeId}`);
        if (element) {
          const navbarHeight = 64; // navbar height
          const additionalOffset = 12; // additional spacing
          const totalOffset = navbarHeight + additionalOffset;

          const elementPosition =
            element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - totalOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, []);

  // Function to get CSS variable values for theme-aware Mermaid colors
  const getThemeColors = () => {
    try {
      const root = document.documentElement;
      const style = getComputedStyle(root);

      // Get DaisyUI CSS variables with fallbacks
      const primary = style.getPropertyValue("--p")?.trim() || "59 130 246";
      const primaryContent =
        style.getPropertyValue("--pc")?.trim() || "255 255 255";
      const secondary = style.getPropertyValue("--s")?.trim() || "156 163 175";
      const neutral = style.getPropertyValue("--n")?.trim() || "107 114 128";
      const baseContent = style.getPropertyValue("--bc")?.trim() || "31 41 55";

      // Convert space-separated values to hex colors
      const toHex = (spaceDelimited) => {
        try {
          const values = spaceDelimited
            .split(" ")
            .map((val) => parseInt(val.trim(), 10));
          if (
            values.length !== 3 ||
            values.some((v) => isNaN(v) || v < 0 || v > 255)
          ) {
            return "#3b82f6"; // Default blue
          }
          const [r, g, b] = values;
          return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
        } catch (error) {
          console.warn("Error converting color to hex:", spaceDelimited, error);
          return "#3b82f6"; // Default blue
        }
      };

      return {
        primary: toHex(primary),
        primaryContent: toHex(primaryContent),
        secondary: toHex(secondary),
        neutral: toHex(neutral),
        baseContent: toHex(baseContent),
      };
    } catch (error) {
      console.warn("Error getting theme colors, using defaults:", error);
      // Return default colors if anything fails
      return {
        primary: "#3b82f6",
        primaryContent: "#ffffff",
        secondary: "#9ca3af",
        neutral: "#6b7280",
        baseContent: "#1f2937",
      };
    }
  };

  // Mermaid chart definition
  const chartDefinition = `
    flowchart TD
        A["🏢<br/>Set NCB Interpol<br/>Indonesia"]
        A --> B["🌐<br/>(1) Bagjatranin<br/>Kejahatan Transnasional<br/>& Internasional"]
        A --> C["📡<br/>(2) Bagkominter<br/>Komunikasi<br/>Internasional"]
        A --> D["🛡️<br/>(3) Bagbatanas<br/>Perbatasan<br/>Internasional"]
        A --> E["📋<br/>(4) Urtu<br/>Urusan Tata Usaha"]
        
        B --> B1["Subbagops<br/>Interpol"]
        B --> B2["Subbagkor<br/>Interpol"]
        B --> B3["Subbag<br/>Bankuminter"]
        B --> B4["Subbag<br/>BI"]
        B --> B5["Urmin<br/>B"]
        
        C --> C1["Subbagtekkom"]
        C --> C2["Subbaginfo<br/>Data"]
        C --> C3["Subbagpubdok"]
        C --> C4["Urmin<br/>C"]
        
        D --> D1["Subbagbatas"]
        D --> D2["Subbagmonevtas"]
        D --> D3["Urmin<br/>D"]
        
        classDef rootNode fill:#1e40af,stroke:#1e3a8a,stroke-width:4px,color:#fff,font-weight:bold
        classDef deptNode fill:#3b82f6,stroke:#2563eb,stroke-width:3px,color:#fff,font-weight:bold,cursor:pointer
        classDef subNode fill:#93c5fd,stroke:#60a5fa,stroke-width:2px,color:#1e40af,font-size:12px
        classDef adminNode fill:#6b7280,stroke:#4b5563,stroke-width:2px,color:#fff
        
        class A rootNode
        class B,C,D deptNode
        class E adminNode
        class B1,B2,B3,B4,B5,C1,C2,C3,C4,D1,D2,D3 subNode
  `;

  const departments = [
    {
      id: "bagjatranin",
      title: "Bagian Kejahatan Transnasional dan Internasional",
      subtitle: "(Bagjatranin)",
      icon: "🌐",
      color: "from-primary to-primary-focus",
      borderColor: "border-primary",
      description:
        "Merencanakan dan mengelola kegiatan kerja sama internasional dalam wadah Interpol maupun dengan lembaga penegak hukum dalam rangka penanggulangan kejahatan transnasional/internasional.",
      functions: [
        "Penyelenggaraan kegiatan dan operasi Interpol",
        "Penyelidikan dan pertukaran informasi kejahatan transnasional",
        "Pemberian bantuan hukum internasional terkait ekstradisi",
        "Pemberian bantuan penanganan buronan internasional",
      ],
      subunits: [
        "Subbagian Operasional Interpol (Subbagops Interpol)",
        "Subbagian Koordinasi Interpol (Subbagkor Interpol)",
        "Subbagian Bantuan Hukum Internasional (Subbagbankuminter)",
        "Subbagian Buronan Internasional (Subbag BI)",
        "Urusan Administrasi (Urmin)",
      ],
    },
    {
      id: "bagkominter",
      title: "Bagian Komunikasi Internasional",
      subtitle: "(Bagkominter)",
      icon: "📡",
      color: "from-success to-success-focus",
      borderColor: "border-success",
      description:
        "Membangun, mengembangkan dan memelihara fasilitas jaringan komunikasi antar penegak hukum di Indonesia dengan negara anggota Interpol dan IPSG serta negara anggota Aseanapol.",
      functions: [
        "Pertukaran informasi kejahatan lintas negara/internasional",
        "Pemublikasian informasi dari Interpol dan Aseanapol",
        "Pengembangan penggunaan jaringan Interpol I-24/7 dan Aseanapol e-ADS",
        "Pelaksana National Security Officer (NSO) Jaringan Interpol",
      ],
      subunits: [
        "Subbagian Teknologi dan Komunikasi (Subbagtekkom)",
        "Subbagian Informasi dan Data (Subbaginfo Data)",
        "Subbagian Publikasi dan Dokumentasi (Subbagpubdok)",
        "Urusan Administrasi (Urmin)",
      ],
    },
    {
      id: "bagbatanas",
      title: "Bagian Perbatasan Internasional",
      subtitle: "(Bagbatanas)",
      icon: "🛡️",
      color: "from-secondary to-secondary-focus",
      borderColor: "border-secondary",
      description:
        "Melaksanakan kerja sama dengan pemangku kepentingan dalam penanggulangan kejahatan transnasional/internasional di wilayah perbatasan darat, laut, pulau terluar, pelabuhan laut dan bandara internasional.",
      functions: [
        "Koordinasi penanggulangan kejahatan di perbatasan darat",
        "Koordinasi penanggulangan kejahatan di perbatasan laut dan pulau terluar",
        "Koordinasi keamanan di pelabuhan laut dan bandara internasional",
        "Monitoring dan evaluasi masalah keamanan perbatasan",
      ],
      subunits: [
        "Subbagian Perbatasan (Subbagbatas)",
        "Subbagian Monitoring dan Evaluasi Perbatasan (Subbagmonevtas)",
        "Urusan Administrasi (Urmin)",
      ],
    },
  ];

  useEffect(() => {
    // Initialize Mermaid with basic configuration
    mermaid.initialize({
      startOnLoad: true,
      theme: "base",
      themeVariables: {
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: "14px",
        primaryColor: "#3b82f6",
        primaryTextColor: "#ffffff",
        primaryBorderColor: "#2563eb",
        lineColor: "#6b7280",
      },
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
        curve: "cardinal",
        nodeSpacing: 50,
        rankSpacing: 60,
        padding: 20,
      },
      securityLevel: "loose", // Allow click events
    });
  }, []);

  useEffect(() => {
    if (showChart && chartRef.current) {
      chartRef.current.innerHTML = "";

      try {
        // Get theme colors for dynamic theming
        const themeColors = getThemeColors();

        // Update mermaid configuration with current theme colors
        mermaid.initialize({
          startOnLoad: true,
          theme: "base",
          themeVariables: {
            fontFamily: "Inter, system-ui, sans-serif",
            fontSize: "14px",
            primaryColor: themeColors.primary,
            primaryTextColor: themeColors.primaryContent,
            primaryBorderColor: themeColors.primary,
            lineColor: themeColors.baseContent,
            background: "transparent",
            mainBkg: themeColors.primary,
            secondaryColor: themeColors.secondary,
            tertiaryColor: themeColors.neutral,
          },
          flowchart: {
            useMaxWidth: true,
            htmlLabels: true,
            curve: "cardinal",
            nodeSpacing: 50,
            rankSpacing: 60,
            padding: 20,
          },
          securityLevel: "loose",
        });

        mermaid
          .render("orgChart", chartDefinition)
          .then(({ svg }) => {
            chartRef.current.innerHTML = svg;

            // Add custom styling and interactions
            const svgElement = chartRef.current.querySelector("svg");
            if (svgElement) {
              svgElement.style.width = "100%";
              svgElement.style.height = "auto";
              svgElement.style.maxWidth = "1200px";
              svgElement.style.maxHeight = "500px";

              // Set up proper containment
              const container = chartRef.current;
              container.style.position = "relative";
              svgElement.style.position = "absolute";
              svgElement.style.top = "50%";
              svgElement.style.left = "50%";
              svgElement.style.transform = "translate(-50%, -50%)";
              svgElement.style.transformOrigin = "center center";

              // Add basic zoom and pan functionality with bounds checking
              let scale = 1;
              let translateX = 0;
              let translateY = 0;
              let isDragging = false;
              let lastX = 0;
              let lastY = 0;

              const applyTransform = () => {
                svgElement.style.transform = `translate(-50%, -50%) translate(${translateX}px, ${translateY}px) scale(${scale})`;
              };

              // Mouse wheel zoom
              svgElement.addEventListener("wheel", (e) => {
                e.preventDefault();
                const delta = e.deltaY > 0 ? 0.9 : 1.1;
                scale *= delta;
                scale = Math.min(Math.max(scale, 0.3), 2); // Reduced zoom range
                applyTransform();
              });

              // Mouse drag pan
              svgElement.addEventListener("mousedown", (e) => {
                if (e.button === 0) {
                  // Left mouse button
                  isDragging = true;
                  lastX = e.clientX;
                  lastY = e.clientY;
                  svgElement.style.cursor = "grabbing";
                  e.preventDefault();
                }
              });

              document.addEventListener("mousemove", (e) => {
                if (isDragging) {
                  const deltaX = e.clientX - lastX;
                  const deltaY = e.clientY - lastY;

                  // Apply bounds checking
                  const containerRect = container.getBoundingClientRect();
                  const maxTranslateX = containerRect.width * 0.3;
                  const maxTranslateY = containerRect.height * 0.3;

                  translateX = Math.min(
                    Math.max(translateX + deltaX, -maxTranslateX),
                    maxTranslateX,
                  );
                  translateY = Math.min(
                    Math.max(translateY + deltaY, -maxTranslateY),
                    maxTranslateY,
                  );

                  lastX = e.clientX;
                  lastY = e.clientY;
                  applyTransform();
                }
              });

              document.addEventListener("mouseup", () => {
                if (isDragging) {
                  isDragging = false;
                  svgElement.style.cursor = "grab";
                }
              });

              // Reset transform when double-clicking
              svgElement.addEventListener("dblclick", () => {
                scale = 1;
                translateX = 0;
                translateY = 0;
                applyTransform();
              });

              svgElement.style.cursor = "grab";

              // Add department node click handlers
              const departmentNodes = svgElement.querySelectorAll("g.node");
              departmentNodes.forEach((node) => {
                const textElement = node.querySelector("text, foreignObject");
                if (textElement) {
                  const text =
                    textElement.textContent || textElement.innerText || "";

                  let deptId = null;
                  if (text.includes("(1)") || text.includes("Bagjatranin")) {
                    deptId = "bagjatranin";
                  } else if (
                    text.includes("(2)") ||
                    text.includes("Bagkominter")
                  ) {
                    deptId = "bagkominter";
                  } else if (
                    text.includes("(3)") ||
                    text.includes("Bagbatanas")
                  ) {
                    deptId = "bagbatanas";
                  }

                  if (deptId) {
                    // Style the node
                    node.style.cursor = "pointer";
                    node.style.transition = "filter 0.2s ease";

                    // Add hover effects
                    node.addEventListener("mouseenter", () => {
                      node.style.filter =
                        "brightness(1.1) drop-shadow(0 2px 4px rgba(0,0,0,0.1))";
                    });
                    node.addEventListener("mouseleave", () => {
                      node.style.filter = "none";
                    });

                    // Add click handler
                    const clickHandler = (e) => {
                      console.log("Department node clicked:", deptId);
                      e.preventDefault();
                      e.stopPropagation();
                      handleNodeClick(deptId);
                    };

                    node.addEventListener("click", clickHandler);

                    // Also add click to child elements to ensure clicks are captured
                    const childElements = node.querySelectorAll("*");
                    childElements.forEach((child) => {
                      child.style.cursor = "pointer";
                      child.addEventListener("click", clickHandler);
                    });
                  }
                }
              });
            }
          })
          .catch((error) => {
            console.error("Error rendering Mermaid chart:", error);
            chartRef.current.innerHTML =
              '<div class="text-center text-base-content/70 py-8">Error loading chart</div>';
          });
      } catch (error) {
        console.error("Error initializing chart:", error);
        chartRef.current.innerHTML =
          '<div class="text-center text-base-content/70 py-8">Error loading chart</div>';
      }
    }
  }, [showChart, handleNodeClick, chartDefinition]);

  return {
    // State
    expandedSections,
    selectedNode,
    showChart,
    chartRef,

    // Data
    departments,
    chartDefinition,

    // Actions
    toggleSection,
    handleNodeClick,
    setShowChart,
  };
};

export default useProfilNCBContent;
